import type { EditorView } from '@codemirror/view';
import {
  HelpCircleIcon,
  PlusIcon,
  Trash01Icon,
} from '@langchain/untitled-ui-icons';
import { Button, Divider, Option, Select } from '@mui/joy';

import { omit } from 'lodash-es';
import { ReactNode, useCallback, useContext, useRef, useState } from 'react';

import { EditConversationViewContext } from '@/Pages/Conversation/utils/EditConversationViewContext';
import {
  AsyncForeignDataTreeView,
  ForeginDataTreeContext,
} from '@/Pages/Graph/src/components/debug/common/foreign-data-tree';
import { SchemaEditor } from '@/Pages/Graph/src/components/debug/common/schema-editor';
import { defaultSchemaRenderer } from '@/Pages/Graph/src/components/debug/common/schema-renderer';
import {
  RenderModeContext,
  useRenderModeCtx,
} from '@/Pages/Graph/src/control/state';
import { CollapsibleAdditionalFields } from '@/Pages/Run/components/RunDetailsIO';
import { Tooltip } from '@/design-system';
import {
  MessageCapability,
  MessageContentPart,
  MessageFields,
  S3DataSchema,
  XOR,
  isMessageContentPartAnthropicToolResult,
  isMessageContentPartTool,
} from '@/types/schema';
import {
  convertMessageContentToText,
  getOpenAIRoleFromLangSmithRole,
  getThinkingTokens,
} from '@/utils/messages';
import { cn } from '@/utils/tailwind';
import { tryParseJSON } from '@/utils/try-parse-json';

import { generateSchemaFromObj } from '../utils/generate-schema-from-obj';
import { CodeData } from './Code/Code';
import CollapsedMessage from './CollapsedMessage';
import { CopyButton } from './CopyButton/CopyButton';
import { VarMappingProps } from './EvaluatorCrudPane/types';
import { ExpandableTraceInformation } from './ExpandableTraceInformation';
import {
  OpenAIFunctionCallKwargsSchema,
  OpenAIToolCallsKwargsSchema,
  OpenAIToolMessageKwargsSchema,
  ToolCallsKwargsSchema,
} from './Message.utils';
import { MessageKwargsOpenAITools } from './MessageToolCalls/MessageKwargsOpenAITools';
import { MessageKwargsTools } from './MessageToolCalls/MessageKwargsTools';
import { ToolEditor } from './MessageToolCalls/ToolEditor';
import { RichTextEditor } from './RichTextEditor/RichTextEditor';
import { RichTextView } from './RichTextEditor/RichTextView';
import { useFancyRendering } from './RichTextEditor/utils/fancyRenderContext/useFancyRender';

export type MessageProps = {
  content: string | Array<MessageContentPart>;
  role?: string;
  onChangeRole?: (role: string) => void;
  name?: string;
  variant?: 'output' | 'reference' | 'conversation';
  headerRight?: ReactNode;
  attachments?: Record<string, S3DataSchema>;
  testid?: string;
  omitMaxHeight?: boolean;
  onCreateEditor?: (view: EditorView) => void;
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  onEditContent?: (value: string | MessageContentPart[]) => void;
  onDelete?: () => void;
  onAddToolCall?: () => void;
  className?: string;
  actions?: ReactNode;
  selectedConversationKey?: string;
  variableMappingProps?: VarMappingProps;
  onVariableCreated?: (variableName: string, inputContent: string) => void;
  existingVariables?: string[];
  enableMarkdown?: boolean;
} & XOR<
  { fields?: MessageFields; onEditFields?: (value: MessageFields) => void },
  {
    kwargs?: MessageFields['additional_kwargs'];
    onEditKwargs?: (value: MessageFields['additional_kwargs']) => void;
  }
>;

function isOpenAIResponsesAPIFunctionCall(
  kwargs?: MessageFields['additional_kwargs'] | MessageFields['tool_calls']
): kwargs is MessageFields['additional_kwargs'] & {
  [key: string]: unknown;
  type: 'function_call';
  arguments: string;
  name: string;
  call_id: string;
} {
  return (
    !Array.isArray(kwargs) &&
    kwargs?.type === 'function_call' &&
    typeof kwargs.arguments === 'string' &&
    typeof kwargs.name === 'string' &&
    typeof kwargs.call_id === 'string'
  );
}

function InferSchemaEditor({
  kwargs,
  onEditKwargs,
}: {
  kwargs: MessageFields['additional_kwargs'];
  onEditKwargs?: (value: MessageFields['additional_kwargs']) => void;
}) {
  if (!kwargs) return null;

  const schema = generateSchemaFromObj(kwargs);

  return (
    <div className="px-4 py-2">
      <SchemaEditor
        input={kwargs}
        onChange={(state) => {
          onEditKwargs?.(state);
        }}
        renderer={defaultSchemaRenderer}
        schema={schema}
        rootSchema={schema}
        options={{
          smartExpand: true,
          hideRequiredLabels: true,
          disableAutoFocus: true,
        }}
      />
    </div>
  );
}

function MessageKwargs(props: {
  role?: string;
  fields?: MessageFields;
  kwargs?: MessageFields['additional_kwargs'] | MessageFields['tool_calls'];
  onCreateEditor?: (view: EditorView) => void;
  onEditKwargs?: (value: MessageFields['additional_kwargs']) => void;
  onEditFields?: (value: MessageFields) => void;
  hasTextContent?: boolean;
  variant?: 'plain' | 'full';
}) {
  const { enableMarkdownInTracing } = useFancyRendering();
  const renderModeState = useRenderModeCtx({
    renderMode: 'pretty',
    enableMarkdown: !!enableMarkdownInTracing,
  });
  if (props.role === 'ai') {
    const fnCall = OpenAIFunctionCallKwargsSchema.safeParse(props.kwargs);

    if (fnCall.success) {
      return (
        <div className={cn(props.role && 'mt-2 border-t border-secondary')}>
          <div className="flex flex-col divide-y divide-secondary">
            <span className="px-4 py-3 font-mono">
              {fnCall.data.function_call.name}
            </span>
            <CodeData
              readOnly
              variant={props.variant}
              value={tryParseJSON(fnCall.data.function_call.arguments, true)}
              onCreateEditor={props.onCreateEditor}
              onChange={
                props.onEditKwargs
                  ? (value) => {
                      props.onEditKwargs?.({
                        function_call: {
                          name: fnCall.data.function_call.name,
                          arguments: value,
                        },
                      });
                    }
                  : undefined
              }
            />
          </div>
        </div>
      );
    }

    const oaiToolCalls = OpenAIToolCallsKwargsSchema.safeParse(props.kwargs);
    if (oaiToolCalls.success && oaiToolCalls.data.tool_calls.length > 0) {
      return (
        <div className={cn(props.role && 'mt-2 border-t border-secondary')}>
          <MessageKwargsOpenAITools
            toolKwargs={oaiToolCalls.data}
            onEdit={
              props.onEditKwargs
                ? (value) => {
                    props.onEditKwargs?.(value);
                  }
                : undefined
            }
          />
        </div>
      );
    }

    const toolCalls = ToolCallsKwargsSchema.safeParse(props.fields);
    if (toolCalls.success && toolCalls.data.tool_calls.length > 0) {
      return (
        <div className={cn(props.role && 'mt-2 border-t border-secondary')}>
          <MessageKwargsTools
            toolKwargs={toolCalls.data}
            onEdit={
              props.onEditFields
                ? (value) => {
                    props.onEditFields?.({
                      ...(props.fields as MessageFields),
                      ...value,
                    });
                  }
                : undefined
            }
          />
        </div>
      );
    }
  }

  if (isOpenAIResponsesAPIFunctionCall(props.kwargs)) {
    return (
      <div className={cn(props.role && 'mt-2 border-t border-secondary')}>
        <MessageKwargsOpenAITools
          toolKwargs={{
            tool_calls: [
              {
                id: props.kwargs.call_id,
                type: 'function',
                function: {
                  name: props.kwargs.name,
                  arguments: props.kwargs.arguments,
                },
              },
            ],
          }}
          onEdit={
            props.onEditKwargs
              ? (value) => {
                  props.onEditKwargs?.(value);
                }
              : undefined
          }
        />
      </div>
    );
  }

  if (
    props.kwargs &&
    'type' in props.kwargs &&
    props.kwargs.type === 'image_generation_call'
  ) {
    return (
      <div className="m-2 flex flex-col gap-2 rounded-lg border border-secondary">
        <CollapsibleAdditionalFields
          kwargs={omit(props.kwargs, 'type')}
          expanded={false}
        />
      </div>
    );
  }

  // ChatAnthropic will return additional kwargs which we should skip rendering
  // TODO: undo when Langchain.JS does not return additional kwargs anymore
  if (props.hasTextContent || !props.kwargs) return null;

  const kwargs = props.kwargs as MessageFields['additional_kwargs']; // Already checked if it's a tool call

  return (
    <div className={cn(props.role && 'mt-2 border-t border-secondary')}>
      {props.onEditKwargs && kwargs ? (
        <InferSchemaEditor kwargs={kwargs} onEditKwargs={props.onEditKwargs} />
      ) : (
        kwargs && (
          <div className="flex flex-col gap-2 break-words p-4">
            <RenderModeContext.Provider value={renderModeState}>
              <ForeginDataTreeContext.Provider
                value={{
                  variant: 'default',
                  streaming: false,
                  location: 'langsmith',
                }}
              >
                <AsyncForeignDataTreeView
                  data={kwargs}
                  expandAll={kwargs?.type === 'computer_call_output'}
                />
              </ForeginDataTreeContext.Provider>
            </RenderModeContext.Provider>
          </div>
        )
      )}
    </div>
  );
}

export const Message = (props: MessageProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const name =
    props.name ?? props.fields?.name ?? props.fields?.additional_kwargs?.name;
  const setToolMessageName = props.onEditFields
    ? (name: string) => {
        if (props.onEditFields) {
          let editBody = {
            content: '',
            ...(props.fields as any),
            name,
          };
          if (props.fields?.additional_kwargs != null) {
            editBody = {
              ...editBody,
              additional_kwargs: { ...props.fields.additional_kwargs, name },
            };
          }
          props.onEditFields?.(editBody);
        }
      }
    : undefined;
  const toolCallId =
    props.fields?.tool_call_id ?? props.fields?.additional_kwargs?.tool_call_id;

  const kwargs =
    props.kwargs ??
    (props.fields?.tool_calls?.length && props.fields?.tool_calls[0].name
      ? props.fields.tool_calls
      : null) ??
    (Object.keys(props.fields?.additional_kwargs || {}).filter(
      (key) => !['tool_call_id', 'name'].includes(key)
    ).length > 0
      ? props.fields?.additional_kwargs
      : {});

  const uploadRef = useRef<(src: string) => void>(null);

  const onEditKwargsViaFields = useCallback(
    (
      value: MessageFields['additional_kwargs'] | MessageFields['tool_calls']
    ) => {
      if (
        props.fields?.tool_calls?.length &&
        props.fields?.tool_calls[0].name
      ) {
        props.onEditFields?.({
          ...props.fields,
          tool_calls: value as MessageFields['tool_calls'],
        });
      } else if (props.fields?.additional_kwargs) {
        props.onEditFields?.({
          ...props.fields,
          additional_kwargs: value as MessageFields['additional_kwargs'],
        });
      }
    },
    [props]
  );

  const onEditKwargs =
    props.onEditKwargs ??
    (props.onEditFields ? onEditKwargsViaFields : undefined);

  const kwargsToRender = Object.keys(kwargs || {}).length > 0 ? kwargs : null;

  const toCopy =
    kwargsToRender && !props.content
      ? JSON.stringify(kwargsToRender, null, 2)
      : convertMessageContentToText(props.content);

  const { fancyRendering, enableMarkdownInTracing } = useFancyRendering();
  const setOpenContext = useContext(EditConversationViewContext);

  const [renderKwargs] = useState(
    onEditKwargs &&
      (kwargsToRender || (props.fields?.tool_calls?.length ?? 0) > 0)
  );

  const isSchemaEditorVisible =
    onEditKwargs &&
    kwargsToRender &&
    !OpenAIFunctionCallKwargsSchema.safeParse(kwargs).success &&
    !OpenAIToolCallsKwargsSchema.safeParse(kwargs).success &&
    !OpenAIToolMessageKwargsSchema.safeParse(kwargs).success &&
    !ToolCallsKwargsSchema.safeParse(props.fields).success;

  const canAddToolCall =
    props.role === 'ai' ||
    (props.role && getOpenAIRoleFromLangSmithRole(props.role) === 'assistant');
  const addToolCallComponent = props.onAddToolCall && canAddToolCall && (
    <Tooltip title="Add tool call" side="top">
      <button
        type="button"
        onClick={props.onAddToolCall}
        className="flex items-center justify-center rounded-md border border-primary px-2 py-1 text-xs transition duration-75 hover:bg-secondary-hover"
      >
        <PlusIcon className="h-4 w-4" />
        Tool Call
      </button>
    </Tooltip>
  );

  const trashIcon = props.onDelete && (
    <Tooltip title="Delete message" side="top">
      <button
        type="button"
        onClick={props.onDelete}
        className="flex items-center justify-center rounded-md p-1 transition duration-75 hover:bg-secondary-hover"
      >
        <Trash01Icon className="h-4 w-4" />
      </button>
    </Tooltip>
  );
  const reasoningContent = getThinkingTokens(props);
  if (props.collapsed && props.onCollapse && props.role) {
    return (
      <CollapsedMessage
        bodyRef={bodyRef}
        testid={props.testid}
        onCollapse={props.onCollapse}
        role={props.role}
        toCopy={toCopy}
        headerRight={props.headerRight}
        actions={props.actions}
        isSchemaEditorVisible={!!isSchemaEditorVisible}
        addToolCallComponent={addToolCallComponent}
        trashIcon={trashIcon}
      />
    );
  }

  const showInputToolCalls =
    props.content instanceof Array &&
    props.content.some(
      (content) =>
        isMessageContentPartAnthropicToolResult(content) ||
        isMessageContentPartTool(content)
    ) &&
    !props.fields?.tool_calls;

  return (
    <div ref={bodyRef} data-testid={props.testid}>
      <div
        className={cn(
          'group rounded-lg border border-secondary',
          props.content != null ||
            Object.keys(props.kwargs ?? {}).length ||
            props.fields?.additional_kwargs?.tool_calls?.length
            ? 'bg-background'
            : 'bg-tertiary',
          // I'm honestly not sure when role is not set but need to remove relative
          // for when role is set because role dropdown gets clipped off otherwise
          !props.role && 'relative',
          props.className
        )}
        data-testid="message-component"
      >
        {props.role ? (
          <div
            className={cn(
              'flex w-full cursor-default items-center justify-between gap-3 px-4 py-3 pb-1',
              typeof props.onCollapse === 'function' &&
                'cursor-pointer hover:bg-secondary-hover'
            )}
            onClick={() => props.onCollapse?.(true)}
          >
            <div className="flex items-center gap-1">
              {props.onChangeRole ? (
                <Select
                  value={props.role}
                  onChange={(_, value) => {
                    if (value) {
                      props.onChangeRole?.(value);
                    }
                  }}
                  slotProps={{
                    root: {
                      sx: {
                        width: 'unset',
                        backgroundColor: 'transparent',
                        border: 'none',
                        padding: 0,
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        minHeight: 32,
                        fontSize: 'xs',
                        marginLeft: '-8px',
                        marginTop: '-4px',
                        marginBottom: '-4px',
                      },
                    },
                    button: {
                      sx: (theme) => ({
                        color: theme.vars.palette.neutral[500],
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }),
                    },
                  }}
                >
                  <Option value="human">Human</Option>
                  <Option value="ai">AI</Option>
                  <Option value="system">System</Option>
                  <Option value="tool">Tool</Option>
                  <Divider />
                  <Option value="chat">Chat</Option>
                </Select>
              ) : (
                <p
                  className="text-xs font-semibold uppercase text-tertiary"
                  style={{ fontVariant: 'small-caps' }}
                >
                  {props.role}
                </p>
              )}
              {props.selectedConversationKey && setOpenContext && (
                <Tooltip
                  side="top"
                  title={
                    <div className="flex flex-col gap-2 p-2 text-sm">
                      Selected input key{' '}
                      <pre>{props.selectedConversationKey}</pre>
                      <Button
                        size="sm"
                        color="neutral"
                        onClick={() => setOpenContext.setOpen(true)}
                      >
                        Edit view
                      </Button>
                    </div>
                  }
                >
                  <HelpCircleIcon className="size-4" />
                </Tooltip>
              )}
            </div>

            <span
              className={cn(
                'flex items-center gap-2 opacity-0 transition-all focus-within:opacity-100 group-hover:opacity-100',
                isSchemaEditorVisible && 'hidden'
              )}
            >
              {props.headerRight}
              {addToolCallComponent}
              {props.content && <CopyButton variant="icon" copy={toCopy} />}
              {props.actions}
              {trashIcon}
            </span>
          </div>
        ) : (
          <div
            className={cn(
              'absolute right-4 top-4 z-[5] flex items-center gap-2 opacity-0 transition-all focus-within:opacity-100 group-hover:opacity-100',
              isSchemaEditorVisible && 'hidden'
            )}
          >
            {props.headerRight}
            {addToolCallComponent}
            {props.content && <CopyButton variant="icon" copy={toCopy} />}
            {props.actions}
            {trashIcon}
          </div>
        )}
        {!isOpenAIResponsesAPIFunctionCall(kwargs) &&
          ['function', 'tool'].includes(props.role ?? '') &&
          (setToolMessageName ? (
            <div className="mt-2 border-y border-secondary">
              <ToolEditor
                name={name ?? ''}
                onChangeName={(name) => {
                  setToolMessageName?.(name);
                }}
                id={
                  props.fields?.tool_call_id ??
                  props.fields?.additional_kwargs?.tool_call_id ??
                  ''
                }
                onChangeId={(id) => {
                  if (props.onEditFields && props.fields) {
                    // Special case for additional kwargs
                    if (props.fields.additional_kwargs?.tool_call_id != null) {
                      props.onEditFields?.({
                        ...props.fields,
                        additional_kwargs: {
                          ...props.fields.additional_kwargs,
                          tool_call_id: id,
                        },
                      });
                    } else {
                      props.onEditFields?.({
                        ...props.fields,
                        tool_call_id: id,
                      });
                    }
                  }
                }}
              />
            </div>
          ) : (
            (name || toolCallId) && (
              <div className="mt-2 border-y border-secondary px-4 py-3">
                <span
                  className="cursor-default font-mono"
                  data-testid={`tool_call_function_name_${name}`}
                >
                  {name}{' '}
                  {toolCallId && (
                    <span className="rounded-md border border-tertiary px-1 py-1 text-sm text-tertiary">
                      {toolCallId}
                    </span>
                  )}
                </span>
              </div>
            )
          ))}

        {props.content || !(kwargsToRender || renderKwargs) ? (
          props.role === 'function' || props.role === 'tool' ? (
            <div>
              <CodeData
                readOnly={!props.onEditContent}
                value={
                  typeof props.content !== 'string'
                    ? props.content
                    : tryParseJSON(props.content, true)
                }
                onChange={props.onEditContent}
              />
            </div>
          ) : (
            <div
              className={cn(
                'overflow-auto',
                !props.omitMaxHeight && 'max-h-[50vh]'
              )}
            >
              {reasoningContent && (
                <ExpandableTraceInformation
                  header="Reasoning"
                  content={reasoningContent}
                />
              )}
              <div style={{ overflowAnchor: 'none' }}>
                <div
                  className={cn(
                    'text-md col-[1] row-[1] m-0 resize-none overflow-hidden whitespace-pre-wrap break-words border-none bg-transparent px-4 py-3',
                    props.role && 'pt-0'
                  )}
                >
                  {props.onEditContent ? (
                    <RichTextEditor
                      initialValue={props.content}
                      autoFocus={false}
                      attachments={props.attachments}
                      onChange={props.onEditContent}
                      capabilities={[MessageCapability.IMAGE]}
                      uploadRef={uploadRef}
                      variableMappingProps={props.variableMappingProps}
                      onVariableCreated={props.onVariableCreated}
                      existingVariables={props.existingVariables}
                    />
                  ) : (
                    <RichTextView
                      content={props.content || 'No data'}
                      attachments={props.attachments}
                      fancyRendering={fancyRendering}
                      enableMarkdown={
                        (props.enableMarkdown || enableMarkdownInTracing) &&
                        props.role === 'ai'
                      }
                      maxHeightForVirtualization={
                        !props.omitMaxHeight ? 'calc(50vh - 18px)' : undefined
                      }
                      className={
                        !props.content &&
                        !Object.keys(props.kwargs ?? {}).length &&
                        !props.fields?.additional_kwargs?.tool_calls?.length
                          ? 'font-style: text-center italic text-tertiary'
                          : ''
                      }
                      toolCalls={props.fields?.tool_calls}
                      showToolCalls={showInputToolCalls}
                    />
                  )}
                </div>
              </div>
              <div className="h-px" style={{ overflowAnchor: 'auto' }} />
            </div>
          )
        ) : null}

        {!Array.isArray(kwargs) && kwargs?.type === 'function_call_output' ? (
          <div>
            <CodeData
              readOnly={!props.onEditContent}
              value={tryParseJSON(kwargs.output, true)}
              onChange={props.onEditContent}
            />
          </div>
        ) : (
          (renderKwargs ||
            kwargsToRender ||
            (props.fields?.tool_calls?.length ?? 0) > 0) && (
            <MessageKwargs
              kwargs={kwargs}
              onEditKwargs={onEditKwargs}
              onEditFields={props.onEditFields}
              fields={props.fields}
              role={props.role}
              hasTextContent={!!props.content}
              onCreateEditor={props.onCreateEditor}
              variant={props.variant === 'conversation' ? 'plain' : 'full'}
            />
          )
        )}
      </div>
    </div>
  );
};
